import { useState, RefObject, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { StepperWC } from '@pypestream/design-system';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
  useProStudioStateMatches,
} from '../../../xstate/app.xstate';
import { useNavigate } from 'react-router';
import { LlmCredentialInput } from '../../../../../../api-services/dist/urql';
import { urqlGql } from '@pypestream/api-services/urql';
import { checkForErrors } from '../../../utils';
interface TaskType {
  name: string;
  description?: string;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
}

interface SolutionFormState {
  botName: string;
  type: 'supervisor' | 'blank';
  brandName?: string;
  knowledgeAgentContext?: string;
  llmCredentialId?: string;
  maxOutputTokens?: number;
  otherInformation?: string;
  supervisorName?: string;
  tasks?: TaskType[];
  temperature?: number;
  tone?: string;
  topK?: number;
  topP?: number;
}

interface FormSolutionInfoType {
  botName: string;
  type: 'supervisor' | 'blank';
  llmCredentialId?: string;
}

interface FormSolutionConfigType {
  brandName?: string;
  supervisorName?: string;
  tone?: string;
  otherInformation?: string;
  temperature?: number;
  maxOutputTokens?: number;
  topP?: number;
  topK?: number;
}

interface FormSolutionTasksType {
  tasks: TaskType[];
}

interface FormSolutionAgentType {
  tone?: string;
  brandName?: string;
}

const defaultFormState: SolutionFormState = {
  botName: '',
  type: 'blank',
};

export const useSolutionWizardForm = (swiperRef: RefObject<StepperWC>) => {
  const [solutionFormState, setSolutionFormState] =
    useState<SolutionFormState>(defaultFormState);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const stepBeforeLoadingRef = useRef<number>(0);

  const { credentials, providers, project } = useProStudioCtxSelector(
    (ctx) => ({
      credentials: ctx.credentials,
      providers: ctx.providers,
      project: ctx.project,
    })
  );

  const isBotFlowError = useProStudioStateMatches(
    'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.botFlowError'
  );

  const initialStepForm = useForm<FormSolutionInfoType>({
    defaultValues: {
      botName: '',
      type: 'blank',
    },
  });

  const createAiProviderStepForm = useForm<LlmCredentialInput>({
    defaultValues: {
      apiKey: '',
      name: '',
      model: '',
      llmProviderId: '',
      endpoint: '',
      projectId: '',
    },
  });

  const configStepForm = useForm<FormSolutionConfigType>({
    defaultValues: {
      supervisorName: '',
      tone: '',
      otherInformation: '',
      temperature: 0.2,
      maxOutputTokens: 2048,
      topP: 0.8,
      topK: 0.2,
    },
  });

  const tasksStepForm = useForm<FormSolutionTasksType>({
    defaultValues: {
      tasks: [],
    },
  });

  const agentStepForm = useForm<FormSolutionAgentType>({
    defaultValues: {
      tone: '',
      brandName: '',
    },
  });

  const addFile = useCallback((file: File) => {
    const newFile: UploadedFile = {
      id: uuidv4(),
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    };
    setUploadedFiles((prev) => [...prev, newFile]);
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  }, []);

  const updateFormState = (formData?: Partial<SolutionFormState>) => {
    if (!formData) {
      return;
    }

    setSolutionFormState((prevState) => ({
      ...prevState,
      ...formData,
    }));
  };

  const createSolution = ({
    formData,
    callback,
    sourceStepIndex,
  }: {
    callback: () => void;
    formData?: Partial<SolutionFormState>;
    sourceStepIndex?: number;
  }) => {
    setIsLoading(true);
    const solutionState = {
      ...solutionFormState,
      ...formData,
    };
    if (solutionState.llmCredentialId === '') {
      solutionState.llmCredentialId = undefined;
    }
    const { botName, type: _, ...versionData } = solutionState;

    stepBeforeLoadingRef.current =
      sourceStepIndex ?? swiperRef?.current?.swiper?.activeIndex ?? 0;

    swiperRef?.current?.goToStep(4);

    sendProStudioEvent({
      type: 'createCustomerBot',
      payload: {
        botName,
        files: uploadedFiles.map((file) => file.file),
        version: {
          ...cloneDeep(versionData),
          // `files` and `tasks` are non-null on SolutionVersionInput in the Agent
          // Manager schema, and Hasura rejects the whole createSolution mutation with
          // "missing required field 'files'" if either is absent. The wizard's own
          // uploads go through uploadSolutionFiles after the solution exists, so an
          // empty array is the correct value here rather than the picked files.
          files: [],
          tasks: versionData.tasks ?? [],
        },
      },
      cb: (res) => {
        if (!res) {
          setIsLoading(false);
          swiperRef?.current?.goToStep(stepBeforeLoadingRef.current);
          return;
        }

        navigate(res);
        callback();
        setIsLoading(false);
        resetFormState();
      },
    });
  };

  const resetFormState = () => {
    setUploadedFiles([]);
    setSolutionFormState(defaultFormState);
    initialStepForm.reset();
    configStepForm.reset();
    tasksStepForm.reset();
    agentStepForm.reset();
    swiperRef?.current?.goToStep(0);
  };

  const handleNextStep = async (
    formData?: Partial<SolutionFormState>,
    isAiProviderStep?: boolean
  ) => {
    updateFormState(formData);
    if (isAiProviderStep) {
      setIsLoading(true);
      const resp = await urqlGql.createCredential({
        credential: {
          ...createAiProviderStepForm.getValues(),
          projectId: project?.id || '',
        },
      });
      setSolutionFormState((prevState) => ({
        ...prevState,
        llmCredentialId: resp.data?.admin_?.createCredential?.id,
      }));
      setIsLoading(false);
      checkForErrors(resp);
    }
    swiperRef?.current?.nextStep();
  };

  const handlePrevStep = () => swiperRef?.current?.backStep();

  const onCloseModal = () => {
    resetFormState();
  };

  return {
    initialStepForm,
    configStepForm,
    tasksStepForm,
    agentStepForm,
    uploadedFiles,
    addFile,
    removeFile,
    updateFormState,
    resetFormState,
    createSolution,
    handleNextStep,
    handlePrevStep,
    createAiProviderStepForm,
    credentials,
    providers,
    onCloseModal,
    isLoading,
    setIsLoading,
    isBotFlowError,
    stepBeforeLoadingRef,
  };
};
